/**
 * Razorpay Payment Utility
 * Handles loading Razorpay script and creating payment handlers
 */

const RAZORPAY_SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';

// Check if Razorpay script is already loaded
let razorpayScriptLoaded = false;

/**
 * Load Razorpay checkout script dynamically
 * @returns {Promise<boolean>}
 */
export const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (razorpayScriptLoaded || window.Razorpay) {
      razorpayScriptLoaded = true;
      resolve(true);
      return;
    }

    const script = document.createElement('script');
    script.src = RAZORPAY_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      razorpayScriptLoaded = true;
      resolve(true);
    };
    script.onerror = () => {
      resolve(false);
    };
    document.body.appendChild(script);
  });
};

/**
 * Create and open Razorpay checkout
 * @param {Object} options - Razorpay options
 * @param {string} options.key - Razorpay Key ID
 * @param {string} options.order_id - Order ID from backend
 * @param {number} options.amount - Amount in paise
 * @param {string} options.currency - Currency (INR)
 * @param {string} options.name - Business name
 * @param {string} options.description - Payment description
 * @param {string} options.prefill_email - Customer email (optional)
 * @param {string} options.prefill_phone - Customer phone (optional)
 * @param {Function} options.onSuccess - Success callback with payment data
 * @param {Function} options.onError - Error callback
 * @param {Function} options.onDismiss - Modal dismissed callback
 */
export const openRazorpayCheckout = async (options) => {
  const scriptLoaded = await loadRazorpayScript();
  
  if (!scriptLoaded) {
    options.onError?.({ error: 'Failed to load Razorpay SDK' });
    return;
  }

  const razorpayOptions = {
    key: options.key,
    amount: options.amount,
    currency: options.currency || 'INR',
    name: options.name || 'EstateCloud',
    description: options.description || 'Payment',
    order_id: options.order_id,
    prefill: {
      email: options.prefill_email || '',
      contact: options.prefill_phone || ''
    },
    theme: {
      color: '#0EA5E9' // Sky-500 to match EstateCloud theme
    },
    handler: function (response) {
      // Payment successful
      options.onSuccess?.({
        razorpay_order_id: response.razorpay_order_id,
        razorpay_payment_id: response.razorpay_payment_id,
        razorpay_signature: response.razorpay_signature
      });
    },
    modal: {
      ondismiss: function () {
        options.onDismiss?.();
      },
      escape: true,
      animation: true
    }
  };

  try {
    const razorpay = new window.Razorpay(razorpayOptions);
    razorpay.on('payment.failed', function (response) {
      options.onError?.({
        error: response.error.description,
        code: response.error.code,
        reason: response.error.reason
      });
    });
    razorpay.open();
  } catch (error) {
    options.onError?.({ error: error.message });
  }
};

/**
 * Format amount for display (paise to rupees)
 * @param {number} amountInPaise 
 * @returns {string}
 */
export const formatAmount = (amountInPaise) => {
  return `₹${(amountInPaise / 100).toLocaleString('en-IN')}`;
};

export default {
  loadRazorpayScript,
  openRazorpayCheckout,
  formatAmount
};
